import React, { Fragment } from 'react';

import type { Route } from 'react-resource-router';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { AiSearchTriggerFloating } from '@atlassian/csm-ai-search-trigger';
import { getMeta } from '@atlassian/help-center-common-util/ajs';

/**
 * Adds a floating CSM AI search trigger if the feature gate is enabled.
 * @param Component - The component to wrap.
 * @returns The wrapped component.
 */
const withFloatingCSMAISearchTrigger = <Props extends Record<string, unknown>>(
    Component: React.ComponentType<Props>
): React.ComponentType<Props> => {
    function WrappedComponent(props: Props) {
        return (
            <Fragment>
                <Component {...props} />
                {!__SERVER__ && (
                    <AiSearchTriggerFloating
                        site={typeof window !== 'undefined' ? window.location.hostname : ''}
                        siteId={getMeta('tenant-id') ?? undefined}
                    />
                )}
            </Fragment>
        );
    }

    WrappedComponent.displayName = `WithFloatingCSMAISearchTrigger(${Component.displayName ?? Component.name})`;

    return WrappedComponent;
};

/**
 * Wraps a route with a floating CSM AI search trigger if the feature gate is enabled.
 * @param route - The route to wrap.
 * @returns The wrapped route.
 */
export const routeWithFeatureGatedFloatingCSMAISearchTrigger = (route: Route) => {
    if (FeatureGates.checkGate('csm_hc_ai_search_trigger') && route.component) {
        return {
            ...route,
            component: withFloatingCSMAISearchTrigger(route.component),
        };
    }

    return route;
};
